"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Sunrise, Users, Settings } from "lucide-react";
import { cn } from "@/lib/utils";

// Four-button nav. Community is a sealed top-level surface — community
// content does not appear on Apiaries, Today, or any other tab. This is
// the only entry point.
const navItems = [
  { href: "/", label: "Apiaries", icon: Home },
  { href: "/today", label: "Today", icon: Sunrise },
  { href: "/community", label: "Community", icon: Users },
  { href: "/settings", label: "Settings", icon: Settings },
];

const HIDDEN_ON_PATHS = ["/signin", "/signup", "/account/change-password"];

export function BottomNav() {
  const pathname = usePathname();

  // Hide on auth screens, and on the inspection-round screen — that's a
  // full-screen field surface where the nav would just steal space and invite
  // accidental taps with gloves on.
  if (HIDDEN_ON_PATHS.some((p) => pathname.startsWith(p)) || pathname.endsWith("/round")) {
    return null;
  }

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 left-0 right-0 z-50 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 px-4 pointer-events-none print:hidden"
    >
      <div className="mx-auto max-w-sm pointer-events-auto">
        <div className="relative rounded-full bg-card/90 backdrop-blur-2xl border border-walnut/[0.08] shadow-raised">
          <ul className="flex items-stretch justify-between px-1.5 py-1.5">
            {navItems.map((item) => {
              const isActive =
                item.href === "/"
                  ? pathname === "/" || pathname.startsWith("/apiaries")
                  : pathname.startsWith(item.href);

              return (
                <li key={item.href} className="flex-1">
                  <Link
                    href={item.href}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "relative flex flex-col items-center justify-center gap-0.5 h-12 rounded-full transition-colors duration-150",
                      isActive
                        ? "text-ink"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-0 rounded-full bg-honey-light"
                      />
                    )}
                    <item.icon
                      className="h-[18px] w-[18px] relative z-10"
                      strokeWidth={isActive ? 2.2 : 1.8}
                    />
                    <span className="relative z-10 text-[10px] font-medium tracking-wide">
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
}
